import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';


const Form941ScheduleB = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 941 Schedule B: Deadlines, Rules & 2026 Updates"
          description="Form 941 Schedule B: 2026 Deadlines, Rules & Penalties! Effortless E-filing & IRS compliance with TaxZerone."
          keywords="Form 941, Payroll tax filing, IRS Form 941, Employment tax, Quarterly tax reporting"
          canonical="resources/employment-tax/form-941-schedule-b-filing-requirements/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 941</span>
          </span>{' '}
          Schedule B
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
         <Container maxWidth="lg" sx={{ mb: 0 }}>
          {process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production' && (
            <DynamicBreadcrumbs />
          )}
          <Grid container spacing={0} rowSpacing={0} m={0} sx={{flexDirection:'row-reverse'}}>
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                 <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
borderLeft:'5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
		    
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-941-schedule-b-filing-requirements/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href="https://www.taxzerone.com/"
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 941 Schedule B at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="1"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <b>Form 941 Schedule B</b>
                        {' is used by employers who are '}
                        <b>semiweekly schedule depositors</b>
                        {
                          ' to report their federal tax liability for each day of the quarter. It must generally be filed with Form 941 by employers who reported '
                        }
                        <b>
                          more than $50,000 in employment taxes during the
                          lookback period
                        </b>
                        {' or reached the '}
                        <b>$100,000 next-day deposit threshold</b>.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="2"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <b>Due Date:</b>
                        {
                          ' Schedule B does not have a separate filing deadline. It must be submitted '
                        }
                        <b>with Form 941</b>
                        {' by the quarterly due date: '}
                        <b>April 30, July 31, October 31, or January 31</b>.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="3"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <b>Filing Requirement:</b>
                        {
                          ' Employers who become semiweekly schedule depositors during a quarter must '
                        }
                        generally complete
                        {' Schedule B for the '}
                        <b>entire quarter</b>
                        {
                          ". Schedule B should not be used to report federal tax deposits; it reports the employer's "
                        }
                        <b>daily tax liabilities</b>.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                      Form 941 Schedule B is used by semiweekly schedule
                      depositors who report more than $50,000 in payroll taxes.
                      Businesses that incur more than $100,000 in obligations on
                      a single day of the tax year must also file this 941
                      Schedule B.
                    </Typography>
                  </Box>
                  {/* <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  ></Box> */}
                </Stack>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is IRS Form 941 Schedule B?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whomust" sx={{ color: '#0202ff' }}>
                          Who must file Form 941 Schedule B?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whyschb" sx={{ color: '#0202ff' }}>
                          Why should you File Form 941 Schedule B?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to file Schedule B with Form 941?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#deadsch941" sx={{ color: '#0202ff' }}>
                          When is the Deadline to file Form 941 Schedule B?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#anypenalty" sx={{ color: '#0202ff' }}>
                          Are there any penalties for not filing Schedule B Form
                          941?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#newyearform941" sx={{ color: '#0202ff' }}>
                          What are the new Form 941 updates for 2026 Tax Year?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#e-filef-941" sx={{ color: '#0202ff' }}>
                          E-file Form 941 with TaxZerone in Minutes
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="whatis"
                >
                  What is IRS Form 941 Schedule B?
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  Schedule B, which accompanies Form 941, is a daily report of
                  the employer&apos;s tax liability for federal income taxes
                  withheld from employees. It also includes the employer&apos;s
                  share of the Social Security and Medicare taxes withheld
                  during the period. This withholding information must be
                  submitted to the IRS quarterly using Form 941 Schedule B.
                  Don&apos;t use IRS Form 941 Schedule B to report federal tax
                  deposits.
                </Typography>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 5,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    With TaxZerone, e-File Form 941 along with Schedule B in
                    minutes at just <b>$6.99/form</b>.<br />
                    Also, get an instant filing status.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941schb`}
                  >
                    E-file Form 941 now
                  </Button>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="whomust"
                >
                  Who must file Form 941 Schedule B?
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                  }}
                >
                  Not all employers are required to file Schedule B with their
                  quarterly tax return (Form 941). Schedule B is specifically
                  required for employers who are classified as semiweekly
                  schedule depositors.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                  }}
                >
                  You must file Schedule B 941 if you:{' '}
                </Typography>
                <Typography variant="body1" component="div">
                  <ul style={{ marginBottom: 32 }}>
                    <li style={{ marginBottom: 16 }}>
                      Reported more than $50,000 of employment taxes in the
                      lookback period, OR
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Accumulated a tax liability of $100,000 or more on any
                      given day in the current or prior calendar year
                    </li>
                  </ul>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                  }}
                >
                  If you become a semiweekly schedule depositor at any point
                  during a quarter, you need to complete Schedule B for the
                  entire quarter.{' '}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                  }}
                >
                  The $100,000 tax liability threshold that triggers the
                  next-day deposit requirement is calculated before considering
                  any reduction from nonrefundable credits.{' '}
                </Typography>
                <Alert
                  severity="warning"
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b>&nbsp; Don&apos;t complete Schedule B if you have
                  a tax liability on Form 941, line 12, that is less than $2,500
                  during the quarter.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="whyschb"
                >
                  Why should you File Form 941 Schedule B?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  If you are a semiweekly schedule depositor, you must complete
                  Form 941 Schedule B so that the IRS can reconcile your
                  company&apos;s wages and withholdings and process your tax
                  payments.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="howto"
                >
                  How to file Schedule B with Form 941?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  You have 2 different options for{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-941/"
                    sx={{ color: '#0202ff' }}
                  >
                    filing IRS Form 941
                  </Link>{' '}
                  Schedule B:
                </Typography>
                <Typography variant="body1" component="div">
                  <ul style={{ marginBottom: 32 }}>
                    <li style={{ marginBottom: 16 }}>
                      <span style={{ fontWeight: 600 }}>
                        Paper Filing (By Mail):
                      </span>{' '}
                      If you choose paper filing, you must send both Form 941
                      and Schedule B through traditional mail. It is crucial to
                      note that this procedure may take longer and requires
                      careful attention to ensure accurate and timely submission
                      to the IRS.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <span style={{ fontWeight: 600 }}>
                        Electronic Filing (IRS-Recommended):
                      </span>{' '}
                      The most convenient and effective option is to file both
                      Schedule B and Form 941 electronically. This option
                      simplifies the process and ensures timely filing of tax
                      paperwork.
                    </li>
                  </ul>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="deadsch941"
                >
                  When is the Deadline to file Form 941 Schedule B?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  There is no specified deadline for submitting IRS 941 form
                  Schedule B. It must be submitted along with your Form 941 by
                  the quarterly deadline, which is the last day of the month
                  after the end of the quarter.
                </Typography>
                <Typography variant="body1" component="div">
                  <ul style={{ marginBottom: 32 }}>
                    <li style={{ marginBottom: 16 }}>
                      <span style={{ fontWeight: 600 }}>April 30th</span> -
                      First quarter Deadline (January, February, and March)
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <span style={{ fontWeight: 600 }}>July 31st</span> -
                      Second quarter Deadline (April, May, and June)
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <span style={{ fontWeight: 600 }}>October 31st</span> -
                      Third quarter Deadline (July, August, and September)
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <span style={{ fontWeight: 600 }}>January 31st</span> -
                      Fourth quarter Deadline (October, November, and December)
                    </li>
                  </ul>
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="anypenalty"
                >
                  Are there any penalties for not filing Schedule B Form 941?
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  Failure to provide a 2026 941 schedule b may make it
                  impossible for the IRS to determine which penalty rates apply.
                  The IRS will typically split the difference by charging
                  average penalties. These are calculated by distributing your
                  total tax due shown on IRS Form 941, line 12, equally
                  throughout the tax period.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="newyearform941"
                >
                  What are the updates to Form 941 for the 2026 tax year?
                </Typography>
                {/* <Typography variant="body1" sx={{ mb: 1 }}>
                  There are some major changes to Form 941 for 2024. The
                  following changes are expected for the 2024 tax year:
                </Typography> */}
                <List sx={{ mb: 1 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        Social security tax rate:
                      </span>{' '}
                      Remains at 6.2% each for employee and employer.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        Social security wage base limit:
                      </span>{' '}
                      Increased to $184,500.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        Medicare tax rate:
                      </span>{' '}
                      Unchanged at 1.45% each for employee and employer with no
                      wage base limit.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        Household workers threshold:
                      </span>{' '}
                      Social security and Medicare taxes apply to household
                      workers paid $3,000 or more in cash wages in 2026.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        Election workers threshold:
                      </span>{' '}
                      Social security and Medicare taxes apply to election
                      workers paid $2,500 or more in cash or equivalent
                      compensation in 2026.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        Direct deposit of Form 941 refund:
                      </span>{' '}
                      Following{' '}
                      <Link
                        href="https://www.irs.gov/eo14247"
                        target="_blank"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Executive Order 14247
                      </Link>
                      , the IRS now issues Form 941 refunds by direct deposit
                      for faster and more secure payments.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        Electronic Payments Required:
                      </span>{' '}
                      EO 14247 also mandates electronic payment of any balance
                      due on Form 941.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        Electronic Return Transcripts:
                      </span>{' '}
                      Form 941{' '}
                      <Link
                        href="https://www.irs.gov/businesstranscript"
                        target="_blank"
                        sx={{ color: '#0202ff' }}
                      >
                        transcripts
                      </Link>{' '}
                      for tax years 2023 and later are now available through{' '}
                      <Link
                        href="https://www.irs.gov/BusinessAccount"
                        target="_blank"
                        sx={{ color: '#0202ff' }}
                      >
                        IRS Business Tax Account
                      </Link>
                      .
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        Withholding on qualified tips:
                      </span>{' '}
                      From 2025–2028, the{' '}
                      <span style={{ fontWeight: 600 }}>
                        One Big Beautiful Bill Act
                      </span>{' '}
                      allows employees and self-employed individuals to deduct
                      up to{' '}
                      <span style={{ fontWeight: 600 }}>
                        $25,000 in qualified tips
                      </span>
                      , including cash, credit-card, and tip-sharing amounts.
                      Employees can update their Form W-4 to adjust withholding,
                      and employers should follow{' '}
                      <span style={{ fontWeight: 600 }}>Publication 15-T</span>.
                      Tips remain subject to Social Security and Medicare taxes
                      when monthly tips reach $20 or more and must be reported
                      on{' '}
                      <Link
                        href="/resources/information-returns/form-w-2-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Form W-2
                      </Link>
                      ,{' '}
                      <Link
                        href="/resources/information-returns/form-1099-misc-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-MISC
                      </Link>
                      , or{' '}
                      <Link
                        href="/resources/information-returns/form-1099-nec-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-NEC
                      </Link>
                      .
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        Withholding on qualified overtime compensation:
                      </span>{' '}
                      Employees may also deduct qualified overtime premiums (not
                      the full overtime pay) under the{' '}
                      <span style={{ fontWeight: 600 }}>
                        Fair Labor Standards Act,
                      </span>{' '}
                      up to $12,500 for single filers or $25,000 for joint
                      filers.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  For more information about the changes, see the revised{' '}
                  <Link
                    href="/resources/employment-tax/form-941-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 941 instructions
                  </Link>
                  .
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="e-filef-941"
                >
                  E-file Form 941 Schedule B with TaxZerone in Minutes
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  <Link
                    href="/employment-tax/e-file-irs-form-941/"
                    sx={{ color: '#0202ff' }}
                  >
                    E-file Form 941
                  </Link>{' '}
                  with TaxZerone, along with Schedule B, and get instant updates
                  on the IRS status of your form.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Follow the simple steps below to e-file Form 941.
                </Typography>

                <List sx={{ mb: 1 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Create a{' '}
                      <span style={{ fontWeight: 600 }}>
                        FREE TaxZerone account
                      </span>
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Choose &quot;
                      <span style={{ fontWeight: 600 }}>Form 941</span>&quot;
                      and enter the details.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      If you are a semiweekly depositor,{' '}
                      <span style={{ fontWeight: 600 }}>
                        complete Schedule B
                      </span>
                      .
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        Review and transmit
                      </span>{' '}
                      the return to the IRS.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You will receive an email once the IRS processes your return.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to simplify your quarterly payroll tax filing?
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              <Link
                href="/employment-tax/e-file-irs-form-941/"
                sx={{ color: '#0202ff' }}
              >
                {' '}
                E-file Form 941
              </Link>{' '}
              with the IRS using TaxZerone and stay tax compliant.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941schb`}
              >
                E-file Form 941 Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form941ScheduleB;
